import * as styles from './styles'
import { style as modalStyles } from './components/common/Modal/style'
// import styleSlick from "slick-carousel/slick/slick.css"
import type { Environment, Theme, Themes, TopbarMenu, TopbarMenuItem, Delegations } from './common'
import type { LanguageType } from './hooks/auth'
import { DOMAIN_CONFIG } from './domains'
// Components
import { Box, Btn, Text, Icon, CarouselMulti } from './components/common'
import { Clock } from './components/Clock'
import { ProfileDropdown } from './components/ProfileDropdown'
import { ConfigDropdown } from './components/ConfigDropdown'
import { OrganizationsModal } from './components/OrganizationsModal'
import { MarketModal } from './components/MarketModal'
import { SearchModal } from './components/SearchModal'
import { LockScreenModal } from './components/LockScreenModal'
import { SecurityMessage } from './components/SecurityMessage'
import { ModalMicro } from './components/ModalMicro'
import { NitiaLogo } from './components/NitiaLogo'
// Hooks
import { useAuth } from './hooks/useAuth'
import { useLockScreen } from './hooks/useLockScreen'
import { useOrganizations } from './hooks/useOrganizations'
import { useMarketModal } from './hooks/useMarketModal'
import { useDelegations } from './hooks/useDelegations'
// Utils
import { useRef, useEffect, useState, useMemo } from 'react'
import { emitEvent, getOuterWidth } from './utils'
import { Inbox } from './components/Notifications/Inbox'

export type MicroInfo = {
  id: string,
  baseUrl: string,
  data: any
} | null

export interface AppProps {
  hide?: boolean
  env: Environment
  reload?: boolean
  token?: string
  freeText1?: string
  freeText2?: string
  showDateTime: boolean
  showHelp?: boolean
  showConfiguration?: boolean
  showFindEngine?: boolean
  showLocker?: boolean
  encodeFindEngineText?: boolean
  showFindEngineTextBox?: boolean
  showSwitchTheme?: boolean
  dropdownMenu?: TopbarMenu
  showShoppingCart: boolean
  shoppingCartItems: number
  showFavorites?: boolean
  showNitiaButton?: boolean
  primaryColor?: string
  theme: Theme
  themes?: Themes
  delegations?: Delegations
  secured?: boolean
  state?: string
  micro?: MicroInfo
  onOpenMia?: (props: OpenMiaProps) => void
}

export type OpenMiaProps = {
  language?: LanguageType
}

export type OpenNitiaCuotaProps = {
  showCloseIcon?: boolean
  spendNoticeAmount?: number
}

// Billing picks its root component and modal dimensions from this.
export type BillingMode = 'manage-plans' | 'hire-nitia-cuota'

type toolButton = {
  id: string | null | undefined,
  Component?: JSX.Element,
  Icon?: JSX.Element,
  action?: () => void,
  bullet?: number | string
} | false | null | undefined

const DISABLE_SWITCH_USER = process.env.DISABLE_SWITCH_USER === '1'

export function App({
  hide,
  env,
  token,
  freeText1,
  freeText2,
  showDateTime,
  showHelp,
  showConfiguration,
  showFindEngine,
  showFindEngineTextBox,
  showFavorites,
  showNitiaButton = true,
  encodeFindEngineText,
  showShoppingCart,
  shoppingCartItems,
  showSwitchTheme,
  primaryColor,
  dropdownMenu,
  delegations,
  showLocker,
  theme,
  themes,
  secured,
  state,
  micro,
  onOpenMia
}: AppProps) {

  const ref = useRef<HTMLDivElement>(null)
  const infoSection = useRef<HTMLDivElement>(null)
  const toolSection = useRef<HTMLDivElement>(null)

  // ORGNIZATIONS

  const {
    openModalOrganizations,
    pending: changingOrganization,
    switchOrganization,
    setOpenModalOrganizations
  } = useOrganizations({ ref, state })

  // MARKET MODAL

  const {
    openMarketModal,
    setOpenMarketModal
  } = useMarketModal({ ref })

  // AUTH

  const {
    userToken,
    setToken,
    checkToken,
    resetCheckTimeout,
    logout,
    userInfo,
    config
  } = useAuth({
    token,
    env,
    ref,
    state,
    onSessionBlocked (blocked: boolean) {
      if (DISABLE_SWITCH_USER) return
      setShowLockScreen(blocked)
      if (blocked) { // Close all modals
        setOpenMarketModal(false)
        setOpenModalOrganizations(false)
        setShowSearchModal(false)
      }
    }
  })

   // LOCK SCREEN

   const {
    showLockScreen,
    LockScreenValid,
    LockScreenDisabled,
    lockScreenValue,
    setShowLockScreen,
    unlockScreen,
    blockSession
  } = useLockScreen({
    ref,
    config,
    state,
    onChangeToken: (token: string) => {
      setToken(token)
      resetCheckTimeout()
    }
  })

  // SEARCH

  const [showSearchModal, setShowSearchModal] = useState<boolean>(false)

  useEffect(() => {
    setShowSearchModal(false)
  }, [showFindEngineTextBox])

  // DELEGATIONS MENU

  const {
    activeDelegation,
    setActiveDelegation,
    delegationsMenu
  } = useDelegations({
    delegations,
    userInfo
  })

  // NITIA SHOW BUTTON
  const showIAButton = useMemo(() => {
    if (!showNitiaButton) return false
    if (config.mia_whitelist) return config.mia_whitelist.includes(userInfo.user_uuid)
    else return true
  }, [config, userInfo, showNitiaButton])

  // BUTTONS
  const toolButtons = useMemo(() => {
    return [
      showIAButton && {
        id: 'ia-modal',
        Component: (
          <button
            data-test="ia-modal"
            className="ia-btn"
            onClick={() => onOpenMia?.({ language: userInfo.language })}
          >
            <NitiaLogo size={16} className="ia-btn__logo"/>
            <span className="ia-btn__label">nitia</span>
          </button>
        )
      },
      delegationsMenu && {
        id: 'delegations',
        Component: (
          <ConfigDropdown
            {...delegationsMenu}
            icon="icon-pharmacy"
            theme={theme}
            onClickItem={(item: TopbarMenuItem) => {
              const uuid = item.data?.uuid
              if (item.type === 'menu' || uuid === activeDelegation?.uuid) return
              setActiveDelegation(item.data)
              emitEvent(ref?.current, 'change-delegation', uuid)
            }}
          />
        )
      },
      showShoppingCart && {
        id: 'shopping-cart',
        Icon: <Icon name="icon-shopping-cart-outline" size="xl"/>,
        action: () => emitEvent(ref?.current, 'shopping-cart-pressed', shoppingCartItems),
        bullet: shoppingCartItems
      },
      showFavorites && {
        id: 'favorites',
        Icon: <Icon name="icon-heart-outline" size="xl"/>,
        action: () => emitEvent(ref?.current, 'favorites-button-pressed')
      },
      showHelp && {
        id: 'help',
        Icon: <Icon name="icon-question-mark-circle-outline" size="xl"/>,
        action: () => emitEvent(ref?.current, 'help-button-pressed')
      },
      showConfiguration && {
        id: 'config',
        Icon: <Icon name="icon-settings-2-outline" size="xl"/>,
        action: () => emitEvent(ref?.current, 'configuration-button-pressed')
      },
      dropdownMenu && {
        id: 'dropdown',
        Component: (
          <ConfigDropdown
            {...dropdownMenu}
            theme={dropdownMenu.theme || theme}
            onClickItem={(item: TopbarMenuItem) => {
              if (!item.eventName) return
              emitEvent(ref?.current, `config-${item.eventName}`, item)
            }}
          />
        )
      },
      (showLocker || userInfo.showLocker) && !DISABLE_SWITCH_USER && {
        id: 'locker',
        Icon: <Icon name="icon-lock-outline" size="xl"/>,
        action: () => {
          blockSession(userToken as string)
          setShowLockScreen(true)
          emitEvent(ref?.current, 'screen-locked-by-topbar')
        }
      },
      showFindEngine && {
        id: 'search',
        Icon: <Icon name="icon-search-outline" size="xl"/>,
        action: () => {
          setShowSearchModal(true)
          emitEvent(ref?.current, 'find-engine-pressed')
        }
      },
      {
        id: 'notifications-1',
        Component: (
          <Inbox
            urls={config.urls}
            key="notifications-1"
            userInfo={userInfo}
            userToken={userToken || ''}
            theme="dark"
          />
        ),
      },
      showSwitchTheme && {
        id: 'shitch-theme',
        Icon: <Icon name="icon-theme" size="xl"/>,
        action: () => emitEvent(ref?.current, 'switch-theme-pressed')
      },
    ].map((tool: toolButton, index) => {
      if (!tool) return null
      if (tool.Component) return tool.Component
      return (
        <Btn
          key={index}
          data-test={tool.id}
          className="rounded-0"
          variant="tonal"
          color="icons"
          width="50"
          height="100%"
          bullet={tool.bullet}
          onClick={tool.action}
        >
          {tool.Icon}
        </Btn>
      )
    })
  }, [
    showHelp,
    showConfiguration,
    showFindEngine,
    dropdownMenu,
    delegationsMenu,
    theme,
    showLocker,
    showShoppingCart,
    shoppingCartItems,
    showFavorites,
    showSwitchTheme,
    showIAButton,
    userInfo,
    config
  ])

  // PROFILE

  function getTarget () {
    if (Object.keys(DOMAIN_CONFIG).some((domain) => window.location.origin.includes(domain))) {
      return '_self'
    }
    return '_blank'
  }

  function onClickProfile () {
    const target = getTarget()
    window.open(config.urls?.profile, target)
  }

  function onClickPharmacy () {
    const target = getTarget()
    window.open(config.urls?.my_pharmacy, target)
  }

  // RESPONSIVE

  const [breakpoint, setBreakpoint] = useState<number>(0)
  const [openMenu, setOpenMenu] = useState<boolean>(false)

  useEffect(() => {
    if (!userInfo.loaded || !infoSection.current || !toolSection.current) return
    window.requestAnimationFrame(() => {
      const totalWidth = (
        Array.from(infoSection.current?.children ?? []).reduce((sum, el) => getOuterWidth(el as HTMLElement, 'scrollWidth') + sum, 0) +
        Array.from(toolSection.current?.children ?? []).reduce((sum, el) => getOuterWidth(el as HTMLElement, 'scrollWidth') + sum, 0) +
        30
      )
      setBreakpoint(totalWidth <= 360 ? 0 : totalWidth)
    })
  }, [userInfo, freeText1, freeText2, showDateTime, toolButtons])

  // STYLES

  const isVisibleBar = useMemo(
    () => !!(!hide && userInfo.loaded),
    [userInfo, hide, config]
  )

  const computedStyles = useMemo(() => {
    const { colors, base, ...styleModules } = styles
    return [
      base(breakpoint),
      ...Object.values(styleModules),
      modalStyles,
      colors({
        primary: primaryColor || userInfo.colors?.primary || ''
      }, themes || {})
    ].join('\n')
  }, [userInfo, primaryColor, themes, breakpoint])

  useEffect(() => {
    emitEvent(ref?.current, isVisibleBar ? 'onShowBar' : 'onHideBar')
  }, [isVisibleBar])

  // MICRO

  const [internalMicroId, setInternalMicroId] = useState<string>('')
  const [internalMicroUrl, setInternalMicroUrl] = useState<string>('')

  function onChangeMicro (microId: string, baseUrl: string) {
    setInternalMicroId(microId || '')
    setInternalMicroUrl(baseUrl || '')
  }

  useEffect(() => {
    setInternalMicroId((micro?.id) || '')
    setInternalMicroUrl((micro?.baseUrl) || '')
  }, [micro])

  const microData = useMemo(
    () => ({ ...micro?.data, isAdminUser: userInfo.isAdminUser }),
    [micro?.data, userInfo.isAdminUser]
  )

  return (
    <>
      <style>{computedStyles}</style>
      <div className="topbar-root">
        {userToken && micro && userInfo.loaded && (
          <ModalMicro
            root={ref?.current}
            microId={internalMicroId}
            baseUrl={internalMicroUrl}
            data={microData}
            userInfo={userInfo}
            onChange={onChangeMicro}
            checkToken={checkToken}
          />
        )}
        {userToken && !DISABLE_SWITCH_USER && (
          <LockScreenModal
            active={showLockScreen}
            value={lockScreenValue}
            valid={LockScreenValid}
            disabled={LockScreenDisabled}
            userInfo={userInfo}
            onClickLogout={() => {
              setShowLockScreen(false)
              logout()
            }}
            onChange={(pin: string) => unlockScreen({
              url: config.urls.url_switch_user_pin as string,
              token: userToken as string,
              language: userInfo.language,
              pin
            })}
          />
        )}
        {userToken && (
          <MarketModal
            active={openMarketModal}
            userInfo={userInfo}
            onClose={() => setOpenMarketModal(false)}
          />
        )}
        {userToken && (
          <OrganizationsModal
            active={openModalOrganizations}
            userInfo={userInfo}
            changingOrganization={changingOrganization}
            onClose={() => setOpenModalOrganizations(false)}
            onSwitchOrganization={(uuid: string) => switchOrganization({
              url: config.urls?.url_switch_organization as string,
              token: userToken as string,
              language: userInfo.language,
              uuid
            })}
          />
        )}
        {userToken && showFindEngineTextBox && (
          <SearchModal
            active={showSearchModal}
            theme={theme}
            encodeFindEngineText={encodeFindEngineText}
            onClose={() => setShowSearchModal(false)}
            onSubmit={(text: string) => {
              emitEvent(ref?.current, 'find-engine-submit', text)
              setShowSearchModal(false)
            }}
          />
        )}
        <Box
          ref={ref}
          className="toolbar-app"
          theme={theme}
          bgColor="background"
          color="text"
          style={{ display: isVisibleBar ? 'flex' : 'none' }}
        >
          <Box
            ref={infoSection}
            className="toolbar-app-section section-info"
          >
            <Btn
              className="rounded-0 text-icons"
              color="market"
              width="48"
              height="100%"
              data-test="market"
              onClick={() => userToken && setOpenMarketModal(true)}
            >
              <Icon name="icon-market" size="15"/>
            </Btn>
            <Text className="flex-shrink-0 ml-5" weight="medium">
              Cloud
            </Text>
            {freeText1 && (
              <Text className="topbar-freetext-1 ml-2" weight="bold" data-test="freetext-1">
                {freeText1.substring(0, userInfo.maxFreeText[0] as number)}
              </Text>
            )}
            {showDateTime && (
              <>
                <span className="topbar-clock topbar-divider mx-4"></span>
                <Clock className="topbar-clock flex-shrink-0" language="es" data-test="date-time"/>
              </>
            )}
            {freeText2 && (
              <>
                <span className="topbar-freetext-2 topbar-divider mx-4"></span>
                <Text className="topbar-freetext-2 flex-shrink-0" data-test="freetext-2">
                  {freeText2.substring(0, userInfo.maxFreeText[1] as number)}
                </Text>
              </>
            )}
          </Box>
          <Btn
            className="btn-menu ml-auto"
            variant="text"
            color="menu"
            height="100%"
            data-test="menu"
            onClick={() => setOpenMenu(!openMenu)}
          >
            <Icon
              name={openMenu ? 'icon-close' : 'icon-menu'}
              size={openMenu ? '22' : '21'}
            />
          </Btn>
          <Box
            ref={ref}
            className="toolbar-app"
            theme={theme}
            bgColor="background"
            color="text"
            style={{ display: isVisibleBar ? 'flex' : 'none' }}
          >
            <Box
              ref={infoSection}
              className="toolbar-app-section section-info"
            >
              <Btn
                className="rounded-0"
                color="market"
                width="48"
                height="100%"
                data-test="market"
                onClick={() => userToken && setOpenMarketModal(true)}
              >
                <Icon name="icon-market" color="icons" size="15"/>
              </Btn>
              <Text className="flex-shrink-0 ml-5" weight="medium">
                Cloud
              </Text>
              {freeText1 && (
                <Text className="topbar-freetext-1 ml-2" weight="bold" data-test="freetext-1">
                  {freeText1.substring(0, userInfo.maxFreeText[0] as number)}
                </Text>
              )}
              {showDateTime && (
                <>
                  <span className="topbar-clock topbar-divider mx-4"></span>
                  <Clock className="topbar-clock flex-shrink-0" language="es" data-test="date-time"/>
                </>
              )}
              {freeText2 && (
                <>
                  <span className="topbar-freetext-2 topbar-divider mx-4"></span>
                  <Text className="topbar-freetext-2 flex-shrink-0" data-test="freetext-2">
                    {freeText2.substring(0, userInfo.maxFreeText[1] as number)}
                  </Text>
                </>
              )}
            </Box>
            <Btn
              className="btn-menu ml-auto"
              variant="text"
              color="menu"
              height="100%"
              data-test="menu"
              onClick={() => setOpenMenu(!openMenu)}
            >
              <Icon
                name={openMenu ? 'icon-close' : 'icon-menu'}
                size={openMenu ? '22' : '21'}
              />
            </Btn>
            {userInfo.loaded && (
              <Box className={['toolbar-app-section section-tools ml-auto', { opened: openMenu }]}>
                <CarouselMulti
                  ref={toolSection}
                  disabled={!openMenu}
                  style={{ width: 'calc(100% - 35px)' }}
                >
                  {toolButtons}
                </CarouselMulti>
                <ProfileDropdown
                  className="flex-shrink-0 flex-grow-0 ml-1"
                  userInfo={userInfo}
                  theme={theme}
                  onClickProfile={onClickProfile}
                  onClickPharmacy={onClickPharmacy}
                  onClickLogout={logout}
                  onClickEnv={() => setOpenModalOrganizations(true)}
                />
              </Box>
            )}
          </Box>
        </Box>
        {isVisibleBar && secured && (
          <SecurityMessage />
        )}
      </div>
    </>
  )
}
